import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  backLinkClasses,
  Button,
  Card,
  Field,
  Icon,
  Page,
  PageHeader,
  NotProvided,
  isNotFound,
  NotFoundError,
} from '@evogard/ui';
import type { MemberDetail } from '@evogard/contract';
import { formatInstant } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { MemberStatusBadge } from './MembersScreen.js';
import { Row } from '../../components/Row.js';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * O cadastro de acesso de um associado, e o que a operação pode fazer com ele.
 *
 * ## Quatro ações, quatro permissões
 *
 * Convidar, vincular ao ERP, bloquear e revogar sessões são atos diferentes com
 * consequências diferentes, e cada um tem a própria permissão. **Revogar sessões
 * não é bloquear**: quem atende alguém que perdeu o telefone precisa do primeiro
 * e não deveria ter o segundo.
 *
 * Esconder é cortesia — quem protege é o guard, e chamar a rota direto sem a
 * permissão devolve 403.
 */
export function MemberScreen() {
  const { id } = useParams({ from: '/authenticated/portal/members/$id' });
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  const query = useQuery({
    queryKey: ['portal', 'associado', tenantId, id],
    retry: false,
    queryFn: async () => {
      const r = await api.members.get({ params: { id } });
      if (r.status === 404)
        throw new NotFoundError('Este associado do portal não existe nesta base');
      return ok(r, 200, 'Não foi possível carregar o associado.');
    },
  });

  /*
   * `isPending`, não `isLoading`.
   *
   * `isLoading` é `isPending && isFetching` — e uma consulta que o React Query
   * PAUSA por estar sem rede (`networkMode: 'online'`, o padrão) fica pendente
   * sem estar buscando. Com `isLoading`, os três estados davam `false` ao mesmo
   * tempo, o código caía no `data!` e lia `undefined`: o associado que abre uma
   * demanda no elevador recebia um `TypeError` no lugar do esqueleto, e a tela
   * de falha ainda diagnosticava "a Evogard não respondeu" para uma queda de
   * wi-fi.
   */
  if (query.isPending) {
    return (
      <Page>
        <span className="esqueleto block h-7 w-64" />
        <Card>
          <span className="esqueleto block h-4 w-full max-w-md" />
        </Card>
      </Page>
    );
  }

  if (isNotFound(query.error)) {
    return (
      <MissingRecord
        title={query.error.message}
        list={{ to: '/portal/members', label: 'Ver os associados do portal' }}
      />
    );
  }

  if (query.isError) {
    return (
      <div className="max-w-3xl">
        <Alert tone="erro" title="Não foi possível abrir o cadastro.">
          {query.error.message}{' '}
          <Link
            to="/portal/members"
            activeOptions={{ exact: true }}
            className="underline"
          >
            Voltar para a lista
          </Link>
          .
        </Alert>
      </div>
    );
  }

  return <MemberDetail member={query.data!} />;
}

function MemberDetail({ member: a }: { member: MemberDetail }) {
  const { data: context } = useSessionContext();

  const canInvite = can(context, 'portal.members', 'convidar');
  const canLink = can(context, 'portal.members', 'vincular');
  const canBlock = can(context, 'portal.members', 'bloquear');
  const canRevoke = can(context, 'portal.members', 'revogar_sessoes');

  return (
    <Page>
      <PageHeader
        title={a.nome}
        back={
          <Link
            to="/portal/members"
            activeOptions={{ exact: true }}
            className={backLinkClasses()}
          >
            <Icon nome="seta-esquerda" size={16} />
            Associados do portal
          </Link>
        }
        aside={<MemberStatusBadge situacao={a.situacao} />}
      >
        {a.email}
      </PageHeader>

      <Card title="Cadastro">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <Row label="CPF">{a.cpf ?? <NotProvided />}</Row>
          <Row label="Telefone">{a.telefone ?? <NotProvided />}</Row>
          <Row label="Vinculado em">{formatInstant(a.vinculadoAt)}</Row>
          <Row label="Último acesso">
            {a.lastAccessAt ? (
              formatInstant(a.lastAccessAt)
            ) : (
              // "nunca entrou" é um fato útil, e diferente de "não pode entrar"
              <span className="text-texto-suave">nunca entrou</span>
            )}
          </Row>
          <Row label="Sessões abertas">
            <span className="tabular-nums">{a.sessionsActive}</span>
          </Row>
          {a.blockedAt && <Row label="Bloqueado em">{formatInstant(a.blockedAt)}</Row>}
        </dl>
      </Card>

      {canInvite && <Invite member={a} />}
      {canLink && <CodeErp member={a} />}
      {(canBlock || canRevoke) && (
        <Access member={a} canBlock={canBlock} canRevoke={canRevoke} />
      )}
    </Page>
  );
}

/**
 * Convidar e reconvidar.
 *
 * O link só volta na resposta **fora de produção**. Em produção ele existe no
 * e-mail e em nenhum outro lugar — e a tela diz isso, em vez de mostrar um campo
 * vazio que parece defeito.
 */
function Invite({ member: a }: { member: MemberDetail }) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: async () => {
      const r = await api.members.invite({ params: { id: a.id }, body: {} });
      return ok(r, 201, 'Não foi possível enviar o convite.');
    },
    onSuccess: (body) => {
      setError(null);
      setLink(body.link);
      void client.invalidateQueries({ queryKey: ['portal'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const alreadyGuest = a.situacao === 'convidado';

  /*
   * Bloqueado não recebe convite, e o botão some.
   *
   * A API recusa — é ela quem protege. O que o botão fazia, enquanto existia,
   * era prometer: quem administra clicava, o link saía, a pessoa escolhia uma
   * senha e o login negava. A tela dizendo antes o que a API vai responder
   * poupa o caminho inteiro.
   */
  const blocked = Boolean(a.blockedAt);

  return (
    <Card
      title="Convite de primeiro acesso"
      descricao={
        blocked
          ? 'Indisponível enquanto o acesso estiver bloqueado.'
          : alreadyGuest
            ? `Há um convite válido até ${formatInstant(a.inviteExpiresAt!)}.`
            : 'Envia o link para a pessoa criar a própria senha.'
      }
    >
      <div className="flex flex-col gap-3">
        {error && <Alert tone="erro">{error}</Alert>}

        {blocked && (
          <Alert tone="alerta">
            Desbloqueie o acesso antes de convidar. Com o bloqueio de pé, o link levaria a
            uma senha que não abre o portal.
          </Alert>
        )}

        {link && (
          <Alert tone="sucesso" title="Convite enviado.">
            <p className="mt-1">
              O link também aparece aqui porque este não é o ambiente de produção:
            </p>
            <code className="mt-2 block break-all rounded-borda bg-superficie-suave px-2 py-1 text-xs">
              {link}
            </code>
          </Alert>
        )}

        {invite.isSuccess && !link && (
          <Alert tone="sucesso" title="Convite enviado.">
            O link foi para o e-mail da pessoa e não aparece aqui.
          </Alert>
        )}

        {alreadyGuest && (
          <p className="text-sm text-texto-suave">
            Enviar de novo <strong className="font-medium text-texto">invalida</strong> o
            link anterior — dois links válidos ao mesmo tempo é um a mais do que precisa
            existir.
          </p>
        )}

        {!blocked && (
          <div>
            <Button
              variant={alreadyGuest ? 'contorno' : 'primario'}
              loading={invite.isPending}
              onClick={() => {
                setError(null);
                invite.mutate();
              }}
            >
              {alreadyGuest ? 'Enviar novo convite' : 'Enviar convite'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function CodeErp({ member: a }: { member: MemberDetail }) {
  const client = useQueryClient();
  const [code, setCode] = useState(a.erpCode ?? '');
  const [error, setError] = useState<string | null>(null);

  /*
   * O que ficou gravado no último salvamento bem-sucedido.
   *
   * Sem isto o formulário salvava em silêncio: o campo já mostrava o texto
   * digitado antes de clicar, então depois de clicar a tela ficava idêntica ao
   * que era antes — e quem administra não tinha como distinguir "salvou" de
   * "o clique não pegou". A saída é clicar de novo, ou recarregar para
   * conferir. Vale para desvincular também, que é o caso em que o campo fica
   * vazio nas duas situações.
   */
  const [saved, setSaved] = useState<string | null | undefined>(undefined);

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.members.linkErp({
        params: { id: a.id },
        body: { erpCode: code.trim() || null },
      });
      return ok(r, 200, 'Não foi possível vincular.');
    },
    onSuccess: (body) => {
      setError(null);
      setSaved(body.erpCode);
      void client.invalidateQueries({ queryKey: ['portal'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  /*
   * A confirmação some assim que o campo deixa de ser o que foi gravado. Uma
   * mensagem de "salvo" ao lado de um texto que ninguém salvou ainda é pior do
   * que não ter mensagem nenhuma.
   */
  const confirmation =
    saved !== undefined && code.trim() === (saved ?? '')
      ? saved
        ? `Vinculado ao código ${saved}.`
        : 'Desvinculado do ERP.'
      : null;

  return (
    <Card
      title="Código no ERP"
      descricao="Liga este acesso ao cadastro da pessoa no sistema da Evogard."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        {error && (
          <div className="w-full">
            <Alert tone="erro">{error}</Alert>
          </div>
        )}
        {!error && confirmation && (
          <div className="w-full">
            <Alert tone="sucesso">{confirmation}</Alert>
          </div>
        )}
        <div className="min-w-56 flex-1">
          <Field
            label="Código"
            maxLength={60}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Como aparece no Hinova"
            help="Vazio desvincula. Um código ativo por base."
          />
        </div>
        <Button type="submit" loading={save.isPending}>
          Salvar
        </Button>
      </form>
    </Card>
  );
}

function Access({
  member: a,
  canBlock,
  canRevoke,
}: {
  member: MemberDetail;
  canBlock: boolean;
  canRevoke: boolean;
}) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState<number | null>(null);

  const invalidate = () => client.invalidateQueries({ queryKey: ['portal'] });

  const block = useMutation({
    mutationFn: async (blocked: boolean) => {
      const r = await api.members.block({
        params: { id: a.id },
        body: { blocked },
      });
      return ok(r, 200, 'Não foi possível alterar o bloqueio.');
    },
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const revoke = useMutation({
    mutationFn: async () => {
      const r = await api.members.revokeSessions({
        params: { id: a.id },
        body: undefined,
      });
      return ok(r, 200, 'Não foi possível derrubar as sessões.');
    },
    onSuccess: (body) => {
      setError(null);
      setEnded(body.ended);
      void invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const blocked = Boolean(a.blockedAt);

  return (
    <Card title="Acesso">
      <div className="flex flex-col gap-3">
        {error && <Alert tone="erro">{error}</Alert>}

        {ended !== null && (
          <Alert tone="sucesso">
            {ended === 0
              ? 'Não havia sessão aberta para derrubar.'
              : `${ended === 1 ? '1 sessão encerrada' : `${ended} sessões encerradas`}.`}
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {canBlock && (
            <Button
              variant={blocked ? 'contorno' : 'perigo'}
              loading={block.isPending}
              onClick={() => {
                setError(null);
                /*
                 * A confirmação do "derrubar" sai da tela antes de bloquear.
                 * Ela conta quantas sessões AQUELA ação encerrou; deixá-la ao
                 * lado do resultado do bloqueio faz parecer que o bloqueio
                 * encerrou aquelas sessões — e o número seria de outra ação.
                 */
                setEnded(null);
                block.mutate(!blocked);
              }}
            >
              {blocked ? 'Desbloquear acesso' : 'Bloquear acesso'}
            </Button>
          )}

          {/*
           * Ação própria, e não parte do bloqueio: derrubar o aparelho de quem
           * perdeu o telefone é pedido de suporte comum; bloquear a conta é
           * decisão da operação.
           */}
          {canRevoke && (
            <Button
              variant="contorno"
              loading={revoke.isPending}
              disabled={a.sessionsActive === 0}
              onClick={() => {
                setError(null);
                revoke.mutate();
              }}
            >
              Derrubar {a.sessionsActive === 1 ? 'a sessão' : 'as sessões'}
            </Button>
          )}
        </div>

        <p className="text-sm text-texto-suave">
          {blocked
            ? 'Bloqueado: a pessoa não entra e as sessões que existiam foram encerradas.'
            : 'Bloquear encerra na hora as sessões abertas — a sessão vive no banco, não num token que sobrevive ao bloqueio.'}
        </p>
      </div>
    </Card>
  );
}
